import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/api";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/format";
import { Search, ChevronLeft, ChevronRight, Library } from "lucide-react";

export default function SchemeMaster() {
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);

  useEffect(() => { adminApi.get("/admin/schemes/stats").then((r) => setStats(r.data)); }, []);

  const fetchSchemes = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 15 };
      if (search) params.search = search;
      const { data } = await adminApi.get("/schemes", { params });
      setData(data);
    } finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { const t = setTimeout(fetchSchemes, 300); return () => clearTimeout(t); }, [fetchSchemes]);
  useEffect(() => setPage(1), [search]);

  return (
    <AdminLayout>
      <h1 className="mb-1 font-heading text-3xl text-[var(--brand)]">Scheme Master</h1>
      <p className="mb-6 text-sm text-[var(--ink-muted)]">Regular plans only</p>

      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Card className="glass-card border-0"><CardContent className="p-4"><Library className="h-4 w-4 text-[var(--cta)]" /><div className="mt-2 font-heading text-2xl text-[var(--brand)]" data-testid="scheme-master-total">{stats.total.toLocaleString("en-IN")}</div><div className="text-xs text-[var(--ink-muted)]">AMFI scheme master (all AMCs)</div></CardContent></Card>
          {stats.by_type.slice(0, 3).map((t) => (
            <Card key={t.scheme_type} className="border-[var(--glass-border)]"><CardContent className="p-4"><div className="mt-2 font-heading text-2xl text-[var(--brand)]">{t.count.toLocaleString("en-IN")}</div><div className="text-xs text-[var(--ink-muted)]">{t.scheme_type}</div></CardContent></Card>
          ))}
        </div>
      )}

      <Card className="mb-4 border-[var(--glass-border)]">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--ink-muted)]" />
            <Input className="pl-9" placeholder="Search scheme or AMC name..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-scheme-search-input" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-[var(--glass-border)]">
        <CardContent className="p-0" data-testid="admin-scheme-table">
          {loading ? <div className="p-6 text-sm text-[var(--ink-muted)]">Loading...</div> :
            <div className="divide-y divide-[var(--glass-border)]">
              {data.items.map((s) => (
                <div key={s.scheme_code} className="flex flex-col gap-2 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="font-medium">{s.scheme_name}</div>
                    <div className="text-xs text-[var(--ink-muted)]">{s.amc_name} &middot; {s.scheme_code}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="rounded-full text-[10px]">Regular</Badge>
                    <span className="font-medium text-[var(--brand)]">{s.transaction_metadata_verified ? formatCurrency(s.min_purchase_amt) : "Verify at BSE"}</span>
                  </div>
                </div>
              ))}
            </div>}
        </CardContent>
      </Card>

      {data.total_pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-scheme-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm text-[var(--ink-muted)]">Page {page} of {data.total_pages}</span>
          <Button variant="outline" size="icon" disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)} data-testid="admin-scheme-next-page"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}
    </AdminLayout>
  );
}
